import React, { useEffect, useId, useRef, useState } from "react";
import { sourceDisplayName, sourceTypeLabel, sourceUsageLabel } from "./taskProductDisplay";

export function TaskSectionHeader({ title, description, actions = [], testId }) {
  return (
    <div className="full-task-section-head" data-testid={testId}>
      <div className="full-task-section-copy">
        <h3>{title}</h3>
        {description ? <p className="full-task-section-desc">{description}</p> : null}
      </div>
      {actions.length ? (
        <div className="full-task-section-tools">
          {actions.map((action) => (
            <button
              key={action.key || action.label}
              type="button"
              className={action.primary ? "full-task-btn full-task-btn-secondary" : "full-task-btn full-task-btn-quiet"}
              data-testid={action.testId}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function EmptySectionState({ children, testId }) {
  return (
    <p className="full-task-empty" data-testid={testId}>
      {children}
    </p>
  );
}

export function EvidenceBadge({ kind, label }) {
  const text =
    label ||
    ({
      automated_test: "Automated test",
      ci: "CI",
      runtime: "Runtime check",
      documentation: "Documentation",
      human: "Human review",
    }[kind] ||
      "Human review");
  return (
    <span className="full-task-badge" data-testid="full-task-evidence-badge" data-kind={kind || "human"}>
      {text}
    </span>
  );
}

export function UsageBadge({ usage }) {
  return (
    <span className="full-task-badge" data-testid="full-task-usage-badge" data-usage={usage || "context"}>
      {sourceUsageLabel(usage)}
    </span>
  );
}

export function ItemActionsMenu({
  label = "More actions",
  items = [],
  testId,
  align = "end",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.document.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!items.length) return null;

  return (
    <div className={`full-task-menu ${align === "start" ? "is-start" : ""}`} ref={rootRef} data-testid={testId}>
      <button
        ref={buttonRef}
        type="button"
        className="full-task-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={label}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">•••</span>
      </button>
      {open ? (
        <div className="full-task-menu-panel" role="menu" id={menuId}>
          {items.map((item) => (
            <button
              key={item.key || item.label}
              type="button"
              role="menuitem"
              className={item.danger ? "is-danger" : undefined}
              data-testid={item.testId}
              onClick={() => {
                setOpen(false);
                item.onClick?.();
                buttonRef.current?.focus();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function sourceIcon(type) {
  if (type === "screenshot") return "🖼";
  if (type === "repository") return "⌘";
  if (type === "linked_issue") return "↗";
  if (type === "prd" || type === "document") return "📄";
  if (type === "original_request") return "✎";
  return "•";
}

export function SourceRow({
  item,
  open,
  canMutate,
  onToggleView,
  onEdit,
  onAskAi,
  onRemove,
}) {
  const name = sourceDisplayName(item);
  const typeLabel = sourceTypeLabel(item.type);
  const contributed = typeof item.contributed === "string" ? item.contributed : "";
  return (
    <div className="full-task-source-row" id={`full-task-item-${item.id}`} data-testid="full-task-source">
      <span className="full-task-source-icon" aria-hidden="true">
        {sourceIcon(item.type)}
      </span>
      <div className="full-task-source-main">
        <div className="full-task-source-top">
          <strong className="full-task-source-name" data-testid="full-task-source-name">
            {name}
          </strong>
          <UsageBadge usage={item.usage} />
          <ItemActionsMenu
            label={`Actions for ${name}`}
            testId="full-task-source-menu"
            items={[
              {
                key: "view",
                label: item.href ? "Open" : open ? "Hide details" : "View",
                onClick: () => {
                  if (item.href) window.open(item.href, "_blank", "noopener,noreferrer");
                  else onToggleView?.(item);
                },
              },
              ...(canMutate
                ? [
                    { key: "edit", label: "Edit", onClick: () => onEdit?.(item) },
                    { key: "ask", label: "Ask AI", onClick: () => onAskAi?.(item) },
                    {
                      key: "remove",
                      label: "Remove",
                      danger: true,
                      testId: "full-task-source-remove-btn",
                      onClick: () => onRemove?.(item),
                    },
                  ]
                : []),
            ]}
          />
        </div>
        <p className="full-task-source-meta-line">
          <span className="full-task-source-type" data-testid="full-task-source-type">
            {typeLabel}
          </span>
          {contributed ? (
            <>
              <span className="full-task-source-sep" aria-hidden="true">
                {" "}
                ·{" "}
              </span>
              <span className="full-task-source-used" data-testid="full-task-source-used">
                {contributed}
              </span>
            </>
          ) : null}
        </p>
        {open && item.details ? (
          <p className="full-task-source-details" data-testid="full-task-source-details">
            {item.details}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function TaskItemRow({
  id,
  testId,
  marker,
  title,
  body,
  meta,
  badge,
  warning,
  menuItems,
  menuLabel,
  children,
  className = "",
}) {
  return (
    <div
      className={`full-task-item ${marker ? "" : "is-flush"} ${className}`.trim()}
      id={id}
      data-testid={testId}
    >
      {marker ? (
        <span className="full-task-item-marker" aria-hidden="true">
          {marker}
        </span>
      ) : null}
      <div className="full-task-item-body">
        <div className="full-task-item-top">
          {title ? <strong className="full-task-item-title">{title}</strong> : null}
          {badge || null}
          {menuItems?.length ? <ItemActionsMenu label={menuLabel || `Actions for ${title || "item"}`} items={menuItems} /> : null}
        </div>
        {body ? <p className="full-task-item-text">{body}</p> : null}
        {meta ? <p className="full-task-item-meta">{meta}</p> : null}
        {warning ? (
          <p className="full-task-item-warning" data-testid="full-task-blocking-deferral">
            {warning}
          </p>
        ) : null}
        {children}
      </div>
    </div>
  );
}
